import { useCallback, useState } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  SecurityCallout,
  Stat,
  TextInput,
  ThemeToggle,
  usePollWhileVisible,
} from '@bravemove/ui-web';
import { CompassMark, CompassWordmark } from '@bravemove/design-tokens/brand';
import { adminFetch } from '../auth/apiClient';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';

interface LoginFormValues {
  readonly email: string;
  readonly password: string;
}

/** Result of one `GET /health` probe, measured from this browser. */
interface HealthCheck {
  readonly phase: 'checking' | 'ok' | 'down';
  readonly latencyMs: number | null;
  readonly version: string | null;
  readonly checkedAt: Date | null;
}

const HEALTH_POLL = { intervalMs: 30_000 };

const INITIAL_HEALTH: HealthCheck = {
  phase: 'checking',
  latencyMs: null,
  version: null,
  checkedAt: null,
};

/**
 * F06 — Admin login. Two-pane shell:
 *   left  → credentials form + MoET legal callout
 *   right → "System Health" panel (hidden on `< md` viewports) driven by a
 *           real `GET /health` probe (status + measured round-trip latency +
 *           reported API version) — no decorative fake metrics.
 */
export function LoginPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthCheck>(INITIAL_HEALTH);

  // S-15 — ProtectedRoute stores the originally requested location in
  // `state.from`; thread it through the TOTP / enrollment steps so the admin
  // lands back on the deep link after re-auth instead of the dashboard.
  const fromLocation = (location.state as { from?: Location } | null)?.from;
  const redirectTo = fromLocation
    ? `${fromLocation.pathname}${fromLocation.search}${fromLocation.hash}`
    : '/';

  // S-13 — real system-health probe. One lightweight unauthenticated
  // `GET /health` on mount + every 30s while the tab is visible (F60b); the
  // right pane renders only measured values (status, round-trip latency, API
  // version, last-checked time).
  const checkHealth = useCallback(async () => {
    const started = performance.now();
    try {
      const body = await adminFetch<{ status?: string; version?: string } | null>('/health');
      const version =
        body !== null && typeof body === 'object' && typeof body.version === 'string'
          ? body.version
          : null;
      setHealth({
        phase: 'ok',
        latencyMs: Math.max(0, Math.round(performance.now() - started)),
        version,
        checkedAt: new Date(),
      });
    } catch {
      setHealth({ phase: 'down', latencyMs: null, version: null, checkedAt: new Date() });
    }
  }, []);
  usePollWhileVisible(checkHealth, HEALTH_POLL);

  const schema = z.object({
    email: z.string().email(t('admin:validation.email')),
    password: z.string().min(1, t('admin:validation.required')),
  });

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await auth.login(values.email, values.password);
      if (res.kind === 'totp_required') {
        navigate('/login/totp', {
          state: {
            challengeToken: res.totpChallengeToken,
            securityKeyAvailable: res.securityKeyAvailable,
            from: redirectTo,
          },
          replace: true,
        });
      } else {
        navigate('/totp-enroll', {
          state: { enrollmentToken: res.enrollmentToken, from: redirectTo },
          replace: true,
        });
      }
    } catch (err) {
      setServerError(translateApiError(err, t));
    }
  });

  return (
    <div className="min-h-screen bg-bg grid grid-cols-1 md:grid-cols-[1fr_1.2fr]">
      {/* Left pane — form */}
      <div className="flex flex-col px-8 sm:px-14 py-10 overflow-auto">
        {/* Theme switch kept in the light left pane (the right pane is dark) so
            it's reachable + legible before sign-in; persists across reload. */}
        <div className="flex items-center justify-between">
          <CompassWordmark size={20} />
          <ThemeToggle />
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-[420px] mt-10">
          <div className="text-[11px] font-mono font-bold uppercase tracking-[0.12em] text-accent mb-3">
            {t('admin:login.consoleBanner')}
          </div>
          <h1 className="text-h1 font-bold text-ink leading-[1.02] tracking-tight">
            {t('admin:login.title')}
          </h1>
          <p className="text-body text-ink-2 mt-3 max-w-[360px]">{t('admin:login.subtitle')}</p>

          {serverError ? (
            <Banner tone="danger" className="mt-5">
              {serverError}
            </Banner>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-4 mt-6" noValidate>
            <FormField
              label={t('admin:login.emailLabel')}
              error={form.formState.errors.email?.message}
              required
            >
              <TextInput
                type="email"
                autoComplete="username"
                placeholder={t('admin:login.emailPlaceholder')}
                {...form.register('email')}
              />
            </FormField>
            <FormField
              label={t('admin:login.passwordLabel')}
              error={form.formState.errors.password?.message}
              required
            >
              <TextInput
                type="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
            </FormField>
            <Button type="submit" variant="primary" fullWidth loading={form.formState.isSubmitting}>
              {t('admin:login.submit')}
            </Button>
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() => navigate('/forgot-password')}
              >
                {t('admin:login.forgotPassword')}
              </Button>
            </div>
          </form>

          <SecurityCallout tone="warning" icon="shield" className="mt-7">
            <span className="font-semibold text-ink">{t('admin:login.restrictedHeader')}</span>{' '}
            {t('admin:login.restrictedBody')}
          </SecurityCallout>
        </div>
        <div className="flex justify-between border-t border-hairline pt-4 mt-10 text-[10px] font-mono uppercase tracking-wider text-ink-3">
          <span>{t('admin:login.footerCopyright')}</span>
          <span>{t('admin:login.footerLinks')}</span>
        </div>
      </div>

      {/* Right pane — system health from a real /health probe (hidden < md) */}
      <aside
        aria-label={t('admin:login.systemHealthAriaLabel')}
        className="relative hidden md:flex flex-col justify-between bg-ink text-bg p-10 overflow-hidden"
      >
        {/* Accent glows */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 -start-32 size-[34rem] rounded-full"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 30%, transparent) 0%, transparent 60%)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -end-32 size-[30rem] rounded-full"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--color-accent) 20%, transparent) 0%, transparent 60%)',
          }}
        />

        <div className="relative">
          <div className="text-[11px] font-mono font-bold uppercase tracking-[0.12em] text-accent mb-4">
            {t('admin:login.systemHealthEyebrow')}
          </div>
          {/* S-13 — every value below is measured/reported by the live /health
              probe; the old hard-coded uptime / DB latency / queue depth / GMV
              figures were decorative fakes and are gone. */}
          <div className="grid grid-cols-2 gap-3">
            <Stat
              label={t('admin:login.statApiStatus', { defaultValue: 'API STATUS' })}
              value={
                health.phase === 'ok'
                  ? t('admin:login.statusOperational', { defaultValue: 'Operational' })
                  : health.phase === 'down'
                    ? t('admin:login.statusUnreachable', { defaultValue: 'Unreachable' })
                    : t('admin:login.statusChecking', { defaultValue: 'Checking…' })
              }
              className="!bg-white/[0.06] !border-white/10 !text-bg [&_.text-ink]:!text-bg [&_div]:!text-inherit"
            />
            <Stat
              label={t('admin:login.statLatency', { defaultValue: 'ROUND-TRIP LATENCY' })}
              value={health.latencyMs !== null ? `${health.latencyMs}ms` : '—'}
              className="!bg-white/[0.06] !border-white/10 !text-bg [&_.text-ink]:!text-bg [&_div]:!text-inherit"
            />
            <Stat
              label={t('admin:login.statApiVersion', { defaultValue: 'API VERSION' })}
              value={health.version ? `v${health.version}` : '—'}
              className="!bg-white/[0.06] !border-white/10 !text-bg [&_.text-ink]:!text-bg [&_div]:!text-inherit"
            />
            <Stat
              label={t('admin:login.statCheckedAt', { defaultValue: 'LAST CHECKED' })}
              value={health.checkedAt ? fmtCheckTime(health.checkedAt, locale) : '—'}
              className="!bg-white/[0.06] !border-white/10 !text-bg [&_.text-ink]:!text-bg [&_div]:!text-inherit"
            />
          </div>
        </div>

        <div className="relative rounded-2xl bg-white/[0.06] border border-white/10 p-5 flex items-center gap-5">
          <div className={health.phase === 'down' ? undefined : 'animate-pulse'}>
            <CompassMark size={56} />
          </div>
          <div>
            <div className="text-[20px] font-bold leading-tight tracking-tight">
              {health.phase === 'down'
                ? t('admin:login.healthDownTitle', {
                    defaultValue: 'API unreachable from this browser.',
                  })
                : t('admin:login.heartbeat')}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-bg/70 mt-1">
              {t('admin:login.healthLiveNote', { defaultValue: 'LIVE CHECK · FROM THIS BROWSER' })}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function fmtCheckTime(at: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(at);
  } catch {
    return at.toISOString();
  }
}
